// CRV1 binary container for curve geometry. Little-endian throughout.
// Shared by the build tools (encoder verification) and the browser (decoder),
// so both sides run the exact same decode function.
//
// Header (96 bytes):
//   0  char[4] magic "CRV1"
//   4  u16     version = 1
//   6  u16     header_bytes = 96
//   8  u8      encoding: 0 = F32_RAW, 1 = DELTA_PAL
//   9  u8      dims (2 | 3)
//  10  u8      flags: bit0 closed, bit1 preview
//  11  u8      reserved
//  12  u32     n_points (in this file)
//  16  u32     stride (1 for full; k for preview = every k-th point)
//  20  u32     full_n_points
//  24  u32     palette_count (0 for encoding 0)
//  28  u32     body_bytes
//  32  f32[3]  bbox_min
//  44  f32[3]  bbox_max
//  56  f64[3]  origin (grid origin per axis)
//  80  f64     scale (grid units per world unit; 0 for encoding 0)
//  88  8 bytes reserved
//
// Body, encoding 0: f32[n_points * dims] interleaved.
// Body, encoding 1: i32[dims] q0; i32[palette_count * dims] palette;
//   then n_points-1 codes: u8 b; if b < 255 rank = b else rank = u16 (next 2 bytes).
//   point[i] = origin + (q0 + sum of palette[rank_j] for j<=i) / scale

import type { CurveGeometry } from './types.ts';

export const CRV1_MAGIC = 0x31565243; // "CRV1" read as LE u32
export const CRV1_VERSION = 1;
export const CRV1_HEADER_BYTES = 96;
export const ENC_F32_RAW = 0;
export const ENC_DELTA_PAL = 1;
export const FLAG_CLOSED = 1;
export const FLAG_PREVIEW = 2;

export interface Crv1Header {
  encoding: number;
  dims: 2 | 3;
  closed: boolean;
  preview: boolean;
  nPoints: number;
  stride: number;
  fullNPoints: number;
  paletteCount: number;
  bodyBytes: number;
  bboxMin: number[];
  bboxMax: number[];
  origin: number[];
  scale: number;
}

export function parseHeader(buf: ArrayBuffer, byteOffset = 0): Crv1Header {
  if (buf.byteLength - byteOffset < CRV1_HEADER_BYTES) throw new Error('CRV1: buffer too small for header');
  const dv = new DataView(buf, byteOffset);
  if (dv.getUint32(0, true) !== CRV1_MAGIC) throw new Error('CRV1: bad magic');
  const version = dv.getUint16(4, true);
  if (version !== CRV1_VERSION) throw new Error(`CRV1: unsupported version ${version}`);
  const headerBytes = dv.getUint16(6, true);
  if (headerBytes < CRV1_HEADER_BYTES) throw new Error('CRV1: bad header size');
  const encoding = dv.getUint8(8);
  const dims = dv.getUint8(9);
  if (dims !== 2 && dims !== 3) throw new Error(`CRV1: bad dims ${dims}`);
  const flags = dv.getUint8(10);
  const h: Crv1Header = {
    encoding,
    dims,
    closed: (flags & FLAG_CLOSED) !== 0,
    preview: (flags & FLAG_PREVIEW) !== 0,
    nPoints: dv.getUint32(12, true),
    stride: dv.getUint32(16, true),
    fullNPoints: dv.getUint32(20, true),
    paletteCount: dv.getUint32(24, true),
    bodyBytes: dv.getUint32(28, true),
    bboxMin: [dv.getFloat32(32, true), dv.getFloat32(36, true), dv.getFloat32(40, true)],
    bboxMax: [dv.getFloat32(44, true), dv.getFloat32(48, true), dv.getFloat32(52, true)],
    origin: [dv.getFloat64(56, true), dv.getFloat64(64, true), dv.getFloat64(72, true)],
    scale: dv.getFloat64(80, true),
  };
  if (buf.byteLength - byteOffset !== headerBytes + h.bodyBytes) {
    throw new Error(`CRV1: body length mismatch (expected ${headerBytes + h.bodyBytes}, got ${buf.byteLength - byteOffset})`);
  }
  return h;
}

/** Decode a CRV1 file into geometry. Always returns a fresh Float32Array. */
export function decodeCurve(buf: ArrayBuffer, byteOffset = 0): CurveGeometry {
  const h = parseHeader(buf, byteOffset);
  const D = h.dims;
  const n = h.nPoints;
  const body = byteOffset + CRV1_HEADER_BYTES;
  let coords: Float32Array;

  if (h.encoding === ENC_F32_RAW) {
    if (h.bodyBytes !== n * D * 4) throw new Error('CRV1: raw body size mismatch');
    coords = new Float32Array(n * D);
    coords.set(new Float32Array(buf, body, n * D));
  } else if (h.encoding === ENC_DELTA_PAL) {
    const dv = new DataView(buf, body, h.bodyBytes);
    let o = 0;
    const q = new Array<number>(D);
    for (let d = 0; d < D; d++) { q[d] = dv.getInt32(o, true); o += 4; }
    const pal = new Int32Array(h.paletteCount * D);
    for (let i = 0; i < pal.length; i++) { pal[i] = dv.getInt32(o, true); o += 4; }
    const bytes = new Uint8Array(buf, body + o, h.bodyBytes - o);
    coords = new Float32Array(n * D);
    const scale = h.scale;
    const ox = h.origin[0], oy = h.origin[1], oz = h.origin[2];
    let p = 0;
    if (D === 2) {
      let qx = q[0], qy = q[1];
      coords[0] = ox + qx / scale; coords[1] = oy + qy / scale;
      for (let i = 1, k = 2; i < n; i++, k += 2) {
        let r = bytes[p++];
        if (r === 255) { r = bytes[p] | (bytes[p + 1] << 8); p += 2; }
        const j = r << 1;
        qx += pal[j]; qy += pal[j + 1];
        coords[k] = ox + qx / scale; coords[k + 1] = oy + qy / scale;
      }
    } else {
      let qx = q[0], qy = q[1], qz = q[2];
      coords[0] = ox + qx / scale; coords[1] = oy + qy / scale; coords[2] = oz + qz / scale;
      for (let i = 1, k = 3; i < n; i++, k += 3) {
        let r = bytes[p++];
        if (r === 255) { r = bytes[p] | (bytes[p + 1] << 8); p += 2; }
        const j = r * 3;
        qx += pal[j]; qy += pal[j + 1]; qz += pal[j + 2];
        coords[k] = ox + qx / scale; coords[k + 1] = oy + qy / scale; coords[k + 2] = oz + qz / scale;
      }
    }
    if (p !== bytes.length) throw new Error(`CRV1: index stream length mismatch (${p} vs ${bytes.length})`);
  } else {
    throw new Error(`CRV1: unknown encoding ${h.encoding}`);
  }

  return {
    dims: D,
    nPoints: n,
    closed: h.closed,
    bbox: { min: h.bboxMin, max: h.bboxMax },
    coords,
    stride: h.preview ? h.stride : undefined,
  };
}
